import React, { useState } from 'react';
import { Info, Image } from 'lucide-react';
import drArchitectureImage from '../assets/images/dr-architecture.png';

const DrEnvironmentInfo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDiagram, setShowDiagram] = useState(false);

  const handleLearnMoreClick = () => {
    setIsOpen(!isOpen);
    setShowDiagram(false);
  };

  const handleDiagramClick = () => {
    setShowDiagram(!showDiagram);
    setIsOpen(false);
  };

  return (
    <div className="ml-auto flex gap-2">
      <button
        onClick={handleLearnMoreClick}
        style={{ backgroundColor: '#28A745', color: '#FFFFFF' }}
        className="flex items-center justify-center gap-1 w-custom px-3 py-3 rounded-lg hover:opacity-90"
      >
        <Info size={16} />
        Learn More
      </button>
      <button
        onClick={handleDiagramClick}
        style={{ backgroundColor: '#6F42C1', color: '#FFFFFF' }}
        className="flex items-center justify-center gap-1 w-custom px-3 py-3 rounded-lg hover:opacity-90"
      >
        <Image size={16} />
        Diagram
      </button>


      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 m-4 max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Disaster Recovery Architecture</h3>
                <p className="text-gray-600">Near real-time data replication with rapid failover capability</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Combined Environment */}
              <div className="border rounded-lg p-6 bg-gray-50">
                <h4 className="text-lg font-semibold mb-2">With DR Server</h4>
                <p className="text-sm text-gray-600 mb-4">Separate DR Server</p>

                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-green-900 mb-2">Benefits</h5>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li>• Near real-time database replication (within 10 seconds)</li>
                      <li>• Located in separate availability zone for redundancy</li>
                      <li>• Full server environment mirror of production</li>
                      <li>• Rapid manual failover capability</li>
                    </ul>

                  </div>

                  <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-red-900 mb-2">Limitations</h5>
                    <ul className="space-y-2 text-sm text-green-800">
                      <li>• Cost of additional infrastructure, software licenses, and maintenance</li>
                      <li>• Complexity of setup and management, requiring specialized expertise</li>
                      <li>• Manual failover process, resulting in some downtime</li>
                      <li>• Potential for minimal data loss (seconds) in the event of an unexpected primary server failure</li>
                      <li>• Regular testing needed, which can be time-consuming</li>
                    </ul>

                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-6">
                <h4 className="text-lg font-semibold mb-2">Without DR Server</h4>
                <p className="text-sm text-gray-600 mb-4">Standalone Server</p>

                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-green-900 mb-2">Benefits</h5>
                    <ul className="space-y-2 text-sm text-yellow-800">
                      <li>• Rely on nightly backups only</li>
                      <li>• Potential for up to 24 hours data loss</li>
                      <li>• Longer recovery time</li>
                      <li>• Higher business impact during outages</li>
                    </ul>

                  </div>

                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-amber-900 mb-2">Important Notes</h5>
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li>• Relies on backups only, leading to potential data loss of up to 24 hours</li>
                      <li>• Longer recovery time (RTO) during an outage</li>
                      <li>• Higher business impact due to extended downtime</li>
                      <li>• May not meet compliance requirements for mission-critical systems</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <h4 className="text-lg font-medium text-blue-900 mb-2">When to Choose a DR Server</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• Application is mission-critical and requires minimal downtime (low RTO)</li>
                <li>• Business requires minimal data loss (low RPO) in case of a disaster</li>
                <li>• Compliance requirements mandate a robust DR solution</li>
                <li>• Budget allows for the added cost of a separate DR environment</li>
                <li>• Organization has the IT expertise to manage a DR solution or will use a managed service</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {showDiagram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 m-4 max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-semibold text-gray-900">DR Architecture Diagram</h3>
              <button
                onClick={() => setShowDiagram(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <img 
              src={drArchitectureImage} 
              alt="DR Architecture Diagram" 
              className="w-full h-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DrEnvironmentInfo;